/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 4: Knowledge Selection
 * 
 * Fixed:
 * - Selection bug (was selecting all items)
 * - Better organized layout with cards
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import { getKnowledgePacksByTypes } from '../../../api/contentStudioV2';
import { txt } from '../../../utils/bilingualText';

const Step4Knowledge = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [values, setValues] = useState([]);
  
  // Use Set of IDs for selection tracking to avoid reference issues
  // Note: API returns pack_id, not id
  const [selectedProductIds, setSelectedProductIds] = useState(new Set(
    (sessionData?.selectedProducts || []).map(p => p.pack_id || p.id)
  ));
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set(
    (sessionData?.selectedServices || []).map(p => p.pack_id || p.id)
  ));
  const [selectedValueIds, setSelectedValueIds] = useState(new Set(
    (sessionData?.selectedValues || []).map(p => p.pack_id || p.id)
  ));
  
  const [loading2, setLoading2] = useState(true);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        setLoading2(true);
        const result = await getKnowledgePacksByTypes(token, 'product,service,values');
        const packs = result.packs || [];
        setProducts(packs.filter(p => p.pack_type === 'product'));
        setServices(packs.filter(p => p.pack_type === 'service'));
        setValues(packs.filter(p => p.pack_type === 'values'));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading2(false);
      }
    };
    fetchPacks();
  }, [token, setError]);

  // Get pack unique ID (API uses pack_id, not id)
  const getPackId = (pack) => pack.pack_id || pack.id;

  // Get display name from pack (handles bilingual objects)
  const getPackName = (pack) => txt(pack.name, language, 'Unnamed');

  // Toggle selection using ID-based tracking
  const toggleProduct = (packId) => {
    setSelectedProductIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };

  const toggleService = (packId) => {
    setSelectedServiceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };

  const toggleValue = (packId) => {
    setSelectedValueIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };

  const handleContinue = () => {
    if (selectedProductIds.size === 0) {
      setError(language === 'bm' ? 'Sila pilih sekurang-kurangnya satu produk' : 'Please select at least one product');
      return;
    }
    
    // Convert IDs back to full pack objects for submission
    const selectedProducts = products.filter(p => selectedProductIds.has(getPackId(p)));
    const selectedServices = services.filter(p => selectedServiceIds.has(getPackId(p)));
    const selectedValues = values.filter(p => selectedValueIds.has(getPackId(p)));
    
    setError(null);
    onComplete({ selectedProducts, selectedServices, selectedValues });
  };

  // Select All / Deselect All helpers
  const selectAllProducts = () => {
    setSelectedProductIds(new Set(products.map(p => getPackId(p))));
  };

  const deselectAllProducts = () => {
    setSelectedProductIds(new Set());
  };

  if (loading2) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#fff' }}>Loading knowledge packs...</div>;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          📦 Knowledge Selection
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
          Select knowledge packs for your live content
        </p>
      </div>

      {/* Products Section */}
      <Card style={{ marginBottom: EMZI_SPACING.md, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <CardTitle style={{ fontSize: '16px' }}>🏷️ Products *</CardTitle>
            {products.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={selectAllProducts}
                  style={{ 
                    padding: '4px 10px', 
                    fontSize: '11px', 
                    background: '#E8F4FF', 
                    border: '1px solid #0e67a2', 
                    borderRadius: '4px',
                    color: '#0e67a2',
                    cursor: 'pointer'
                  }}
                >
                  Select All
                </button>
                <button 
                  onClick={deselectAllProducts}
                  style={{ 
                    padding: '4px 10px', 
                    fontSize: '11px', 
                    background: '#fff', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    color: '#666',
                    cursor: 'pointer'
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {selectedProductIds.size} of {products.length} selected
          </p>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <p style={{ color: '#999', fontSize: '13px' }}>No products available</p>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '10px' 
            }}>
              {products.map(pack => {
                const packId = getPackId(pack);
                const isSelected = selectedProductIds.has(packId);
                return (
                  <button
                    key={packId}
                    onClick={() => toggleProduct(packId)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #0e67a2' : '1px solid #e0e0e0',
                      background: isSelected ? '#E8F4FF' : '#fff',
                      color: isSelected ? '#0e67a2' : '#333',
                      fontWeight: isSelected ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: isSelected ? '2px solid #0e67a2' : '2px solid #ccc',
                      background: isSelected ? '#0e67a2' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '12px',
                      flexShrink: 0
                    }}>
                      {isSelected && '✓'}
                    </span>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {getPackName(pack)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Services Section */}
      <Card style={{ marginBottom: EMZI_SPACING.md, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px' }}>🔧 Services</CardTitle>
          {services.length > 0 && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {selectedServiceIds.size} of {services.length} selected
            </p>
          )}
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <p style={{ color: '#999', fontSize: '13px' }}>No services available</p>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '10px' 
            }}>
              {services.map(pack => {
                const packId = getPackId(pack);
                const isSelected = selectedServiceIds.has(packId);
                return (
                  <button
                    key={packId}
                    onClick={() => toggleService(packId)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #0e67a2' : '1px solid #e0e0e0',
                      background: isSelected ? '#E8F4FF' : '#fff',
                      color: isSelected ? '#0e67a2' : '#333',
                      fontWeight: isSelected ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: isSelected ? '2px solid #0e67a2' : '2px solid #ccc',
                      background: isSelected ? '#0e67a2' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '12px',
                      flexShrink: 0
                    }}>
                      {isSelected && '✓'}
                    </span>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {getPackName(pack)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Values Section */}
      <Card style={{ marginBottom: EMZI_SPACING.md, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px' }}>💎 Values</CardTitle>
          {values.length > 0 && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {selectedValueIds.size} of {values.length} selected
            </p>
          )}
        </CardHeader>
        <CardContent>
          {values.length === 0 ? (
            <p style={{ color: '#999', fontSize: '13px' }}>No values available</p>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '10px' 
            }}>
              {values.map(pack => {
                const packId = getPackId(pack);
                const isSelected = selectedValueIds.has(packId);
                return (
                  <button
                    key={packId}
                    onClick={() => toggleValue(packId)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #0e67a2' : '1px solid #e0e0e0',
                      background: isSelected ? '#E8F4FF' : '#fff',
                      color: isSelected ? '#0e67a2' : '#333',
                      fontWeight: isSelected ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: isSelected ? '2px solid #0e67a2' : '2px solid #ccc',
                      background: isSelected ? '#0e67a2' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '12px',
                      flexShrink: 0
                    }}>
                      {isSelected && '✓'}
                    </span>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      {getPackName(pack)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button 
          onClick={onBack} 
          style={{ 
            flex: 1, 
            padding: '14px', 
            background: '#fff', 
            border: '2px solid #0e67a2', 
            borderRadius: '10px', 
            color: '#0e67a2', 
            fontSize: '15px', 
            fontWeight: '600', 
            cursor: 'pointer' 
          }}
        >
          ← Back
        </button>
        <button 
          onClick={handleContinue} 
          style={{ 
            flex: 2, 
            padding: '14px', 
            background: '#F7941E', 
            border: 'none', 
            borderRadius: '10px', 
            color: '#fff', 
            fontSize: '15px', 
            fontWeight: '600', 
            cursor: 'pointer' 
          }}
        >
          Continue to Spider Web →
        </button>
      </div>
    </div>
  );
};

export default Step4Knowledge;
